export class ResultRenderer {
  constructor(containerElement) {
    this.container = containerElement;
  }

  renderEmpty() {
    this.container.innerHTML = `
      <div class="glass-card empty-card">
        <div class="empty-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
        </div>
        <h3>Consulta de Registro</h3>
        <p>Ingrese una CURP para consultar el estatus y documentos correspondientes.</p>
      </div>
    `;
  }

  renderNotFound(query, message) {
    this.container.innerHTML = `
      <div class="glass-card empty-card" style="border-color: rgba(239, 68, 68, 0.3);">
        <div class="empty-icon" style="color: #ef4444;">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
        </div>
        <h3>Sin resultados</h3>
        <p style="margin-bottom: 0.5rem;">No se encontraron registros para la CURP: <strong style="font-family: monospace; color: var(--text-main);">${this.escapeHtml(query)}</strong></p>
        <p style="font-size: 0.85rem; color: var(--text-dim);">${this.escapeHtml(message || 'Verifique que la clave de 18 caracteres esté bien escrita.')}</p>
      </div>
    `;
  }

  renderError(errorMessage) {
    this.container.innerHTML = `
      <div class="glass-card empty-card" style="border-color: rgba(239, 68, 68, 0.4);">
        <div class="empty-icon" style="color: #ef4444;">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        </div>
        <h3>Error en la conexión</h3>
        <p style="color: var(--accent-danger); font-size: 0.9rem;">${this.escapeHtml(errorMessage)}</p>
      </div>
    `;
  }

  renderSuccess(searchResult) {
    const records = searchResult.data || [];
    if (records.length === 0) {
      this.renderNotFound(searchResult.query);
      return;
    }

    const htmlCards = records.map(record => {
      const isNumericFolio = !isNaN(Number(record.folio)) && record.folio.trim() !== '';
      const isBaja = record.dadoDeBaja || !isNumericFolio;
      const motivoBajaText = record.motivoBaja || record.folio;

      if (isBaja) {
        return `
          <div class="glass-card result-card" style="border-color: rgba(239, 68, 68, 0.4);">
            <div class="result-header">
              <div class="curp-badge-group">
                <span class="curp-title">Registro de Beneficiario</span>
                <div class="curp-display">
                  <span>${this.escapeHtml(record.curp)}</span>
                </div>
              </div>
              <div>
                <span class="badge badge-danger">❌ DADO DE BAJA</span>
              </div>
            </div>

            <div class="baja-alert-box">
              <div class="baja-alert-header">
                <span class="baja-icon">🚫</span>
                <span class="baja-title">ESTATUS: DADO DE BAJA EN EL PADRÓN</span>
              </div>
              <div class="baja-reason-container">
                <span class="baja-reason-label">Motivo de Baja:</span>
                <span class="baja-reason-value">${this.escapeHtml(motivoBajaText)}</span>
              </div>
              <p class="baja-subtext">Este registro ha sido cancelado en el programa. No requiere entrega ni cotejo de documentos.</p>
            </div>
          </div>
        `;
      }

      const isCompleted = record.documentos === '1' || record.documentosCompletos === true;
      const docsBadgeClass = isCompleted ? 'badge-success' : 'badge-warning';
      const docsBadgeText = isCompleted ? '✓ Expediente Completo' : '⚠️ Pendientes de Entrega';

      const objetivoDisplay = record.objetivoLabel || this.getObjetivoFallbackLabel(record.objetivo);
      const pendingDocs = record.documentosPendientes || [];

      let docsSectionHtml = '';
      if (!isCompleted && pendingDocs.length > 0) {
        const listItems = pendingDocs.map(doc => `
          <li class="doc-item">
            <span class="doc-bullet">•</span>
            <span class="doc-name">${this.escapeHtml(doc)}</span>
          </li>
        `).join('');

        docsSectionHtml = `
          <div class="pending-docs-box">
            <div class="pending-docs-header">
              <span class="pending-icon">📋</span>
              <span class="pending-title">Documentación Requerida a Entregar (Pendiente):</span>
            </div>
            <ul class="pending-docs-list">
              ${listItems}
            </ul>
          </div>
        `;
      } else {
        docsSectionHtml = `
          <div class="completed-docs-box">
            <span class="completed-icon">✅</span>
            <span>Toda la documentación requerida ha sido entregada y validada satisfactoriamente.</span>
          </div>
        `;
      }

      return `
        <div class="glass-card result-card">
          <div class="result-header">
            <div class="curp-badge-group">
              <span class="curp-title">Beneficiario Registrado</span>
              <div class="curp-display">
                <span>${this.escapeHtml(record.curp)}</span>
              </div>
            </div>
            <div>
              <span class="badge ${docsBadgeClass}">${docsBadgeText}</span>
            </div>
          </div>

          <div class="data-grid">
            <div class="data-item">
              <span class="item-label">Año / Periodo</span>
              <div class="item-value">${this.escapeHtml(record.periodo)}</div>
            </div>

            <div class="data-item">
              <span class="item-label">Folio de Expediente</span>
              <div class="item-value" style="font-family: monospace; color: #818cf8;">#${this.escapeHtml(record.folio)}</div>
            </div>

            <div class="data-item">
              <span class="item-label">No. Padrón (NP)</span>
              <div class="item-value">${this.escapeHtml(record.np || 'N/A')}</div>
            </div>

            <div class="data-item data-item-wide">
              <span class="item-label">Grupo Objetivo</span>
              <div class="item-value-highlight">${this.escapeHtml(objetivoDisplay)}</div>
            </div>
          </div>

          ${docsSectionHtml}
        </div>
      `;
    }).join('');

    this.container.innerHTML = htmlCards;
  }

  getObjetivoFallbackLabel(objetivo) {
    const map = {
      '0': 'ADULTO MAYOR',
      '1': 'ENFERMO CRÓNICO',
      '2': 'MADRE O PADRE SOLTERO CON HIJOS HASTA 12 AÑOS',
      '3': 'MUJER EMBARAZADA',
      '4': 'MUJER EN PERIODO DE LACTANCIA',
      '5': 'PERSONA EN SITUACIÓN DE DISCAPACIDAD'
    };
    const key = String(objetivo || '').trim();
    return map[key] || (key ? key.toUpperCase() : 'DESCONOCIDO');
  }

  escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}
